import React, { useState, useEffect } from 'react';
import ModalDialog from '../components/Dialog/ModalDialog';
import Typography from '../components/Text/Typography';
import Tag from '../components/Tag/Tag';
import Chip from '../components/Chip/Chip';
import Icon from '../components/Icon/Icon';
import LoadingSpinner from '../components/LoadingSpinner';
import './ActiveDetailDialog.css';

interface ActiveDetail {
  active_id: string;
  active_name: string;
  functions: string;
  concern_mapping: string;
  beginner_suggested_frequency: number;
  intermediate_suggested_frequency: number;
  advanced_suggested_frequency: number;
  time_of_day: 'AM' | 'PM' | 'Both';
  typical_percent_low: number;
  typical_percent_high: number;
  skin_compatibility: string;
  skin_incompatibility: string;
  irritation_risk: 'No' | 'Low' | 'Medium' | 'High';
  pregnancy_safe: 'No' | 'Yes';
  combinability: string;
  incombinability: string;
  user_level: 'beginner' | 'intermediate' | 'advanced';
  owned_products: Array<{
    product_id: number;
    product_name: string;
    brand: string;
    category: string;
    image_url: string;
    ingredients: string;
  }>;
}

interface ActiveDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  activeId: string;
}

const ActiveDetailDialog: React.FC<ActiveDetailDialogProps> = ({
  isOpen,
  onClose,
  onBack,
  activeId
}) => {
  const [activeDetail, setActiveDetail] = useState<ActiveDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [originalLevel, setOriginalLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [saving, setSaving] = useState(false);

  // Fetch active detail data
  useEffect(() => {
    if (isOpen && activeId) {
      fetchActiveDetail();
    }
  }, [isOpen, activeId]);

  const fetchActiveDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/user/active-details/${activeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveDetail(data);
        setOriginalLevel(data.user_level);
        setSelectedLevel(data.user_level);
      } else {
        setError('Failed to fetch active ingredient details');
      }
    } catch (error) {
      console.error('Error fetching active detail:', error);
      setError('Error loading active ingredient details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!activeDetail || selectedLevel === originalLevel) return;
    
    setSaving(true);
    try {
      const response = await fetch(`/api/user/active-levels/${activeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`
        },
        body: JSON.stringify({
          level: selectedLevel
        })
      });
      
      if (response.ok) {
        // Update the original level to reflect the saved state
        setOriginalLevel(selectedLevel);
        // Update the active detail with the new level
        setActiveDetail(prev => prev ? { ...prev, user_level: selectedLevel } : null);
      } else {
        setError('Failed to update active ingredient level');
      }
    } catch (error) {
      console.error('Error updating active ingredient level:', error);
      setError('Error updating active ingredient level');
    } finally {
      setSaving(false);
    }
  };

  // Check if there are any changes
  const hasChanges = selectedLevel !== originalLevel;

  if (!isOpen) return null;

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Active ingredients"
      secondaryButton={{
        label: "Back",
        onClick: onBack || onClose
      }}
      primaryButton={{
        label: saving ? "Saving..." : "Save",
        onClick: handleSave,
        disabled: !hasChanges || saving
      }}
    >

      {loading && (
        <div className="loading-state">
          <LoadingSpinner size="small" text="Loading active ingredient details..." />
        </div>
      )}

      {error && (
        <div className="error-state">
          <Typography variant="body" className="error-text">
            {error}
          </Typography>
        </div>
      )}

      {activeDetail && !loading && (
        <div className="active-detail-content">
          <div className="active-info">
            <div className="active-header">
              <Typography variant="h2" className="active-name">
                {activeDetail.active_name}
              </Typography>
              {activeDetail.owned_products.length > 0 && (
                <Tag className="owned-tag">
                  Owned
                </Tag>
              )}
            </div>

            <div className="effects-section">
              <Typography variant="body-bold" className="section-label">
                Effects:
              </Typography>
              <Typography variant="body" className="effects-text">
                {activeDetail.concern_mapping}
              </Typography>
            </div>

            <div className="how-to-use-section">
              <Typography variant="body-bold" className="section-label">
                How to use:
              </Typography>
              <ul className="how-to-use-list">
                <li>
                  <Typography variant="body" className="list-item">
                    {activeDetail.time_of_day === 'AM' ? 'Use in the morning' : 
                     activeDetail.time_of_day === 'PM' ? 'Do not use in the morning (Sun sensitivity)' : 
                     'Can be used morning or evening'}
                  </Typography>
                </li>
                {activeDetail.incombinability && (
                  <li>
                    <Typography variant="body" className="list-item">
                      Avoid using with {activeDetail.incombinability}
                    </Typography>
                  </li>
                )}
                <li>
                  <Typography variant="body" className="list-item">
                    Use {activeDetail.user_level === 'beginner' ? activeDetail.beginner_suggested_frequency :
                         activeDetail.user_level === 'intermediate' ? activeDetail.intermediate_suggested_frequency :
                         activeDetail.advanced_suggested_frequency} times a week
                  </Typography>
                </li>
              </ul>
            </div>

            <div className="owned-products-section">
              <Typography variant="body-bold" className="section-label">
                Owned products:
              </Typography>
              <Typography variant="body" className="owned-products-text">
                {activeDetail.owned_products.length > 0 
                  ? activeDetail.owned_products.map(product => product.product_name).join(', ')
                  : 'N/A'
                }
              </Typography>
            </div>
          </div>

          {/* Experience Level Selector */}
          <div className="experience-level-section">
            <div className="level-chips">
              {(['beginner', 'intermediate', 'advanced'] as const).map((level) => (
                <Chip
                  key={level}
                  label={level.charAt(0).toUpperCase() + level.slice(1)}
                  isSelected={selectedLevel === level}
                  onClick={() => setSelectedLevel(level)}
                  className="level-chip"
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </ModalDialog>
  );
};

export default ActiveDetailDialog;

